/**
 * CashflowPage - Main page for the Cashflow calendar feature
 */

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCashflowStore } from '../stores/cashflowStore';
import CashflowHeader from '../components/cashflow/CashflowHeader';
import CashflowSummary from '../components/cashflow/CashflowSummary';
import CashflowCalendar from '../components/cashflow/CashflowCalendar';
import AIInputModal from '../components/cashflow/AIInputModal';
import PreviewModal from '../components/cashflow/PreviewModal';
import EditEntryModal from '../components/cashflow/EditEntryModal';
import OverduePanel from '../components/cashflow/OverduePanel';
import PaymentScheduleModal from '../components/cashflow/PaymentScheduleModal';
import CreditCardStatementModal from '../components/cashflow/CreditCardStatementModal';
import SpendingAnalysisModal from '../components/cashflow/SpendingAnalysisModal';
import CreditCardSidebar from '../components/cashflow/CreditCardSidebar';

const CashflowPage = () => {
  const {
    isLoading,
    loadEntries,
    isAIModalOpen,
    isPreviewModalOpen,
    isEditModalOpen,
    isOverduePanelOpen,
    isScheduleModalOpen,
    isCreditCardModalOpen,
    isCreditCardSidebarOpen,
    isSpendingAnalysisOpen,
    overdueEntries,
  } = useCashflowStore();

  // Load entries on mount
  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header with navigation and AI input */}
      <CashflowHeader />

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary cards */}
        <CashflowSummary />

        {/* Overdue warning banner */}
        <AnimatePresence>
          {overdueEntries.length > 0 && !isOverduePanelOpen && (
            <OverdueBanner count={overdueEntries.length} />
          )}
        </AnimatePresence>

        {/* Calendar view */}
        <div className="mt-6">
          {isLoading ? (
            <LoadingState />
          ) : (
            <CashflowCalendar />
          )}
        </div>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {isAIModalOpen && <AIInputModal />}
        {isPreviewModalOpen && <PreviewModal />}
        {isEditModalOpen && <EditEntryModal />}
        {isOverduePanelOpen && <OverduePanel />}
        {isScheduleModalOpen && <PaymentScheduleModal />}
        {isCreditCardModalOpen && <CreditCardStatementModal />}
        {isCreditCardSidebarOpen && <CreditCardSidebar />}
        {isSpendingAnalysisOpen && <SpendingAnalysisModal />}
      </AnimatePresence>
    </div>
  );
};

// Overdue warning banner component
const OverdueBanner = ({ count }) => {
  const { toggleOverduePanel } = useCashflowStore();

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mt-4 bg-amber-900/30 border border-amber-500/50 rounded-xl p-4 flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div>
          <p className="text-amber-400 font-medium">
            {count} gecikmiş ödeme
          </p>
          <p className="text-sm text-gray-400">
            Bekleyen ödemeleri kontrol edin
          </p>
        </div>
      </div>
      <button
        onClick={toggleOverduePanel}
        className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors"
      >
        Görüntüle
      </button>
    </motion.div>
  );
};

// Loading state component
const LoadingState = () => (
  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400">Yükleniyor...</p>
    </div>
  </div>
);

export default CashflowPage;
